import React from "react";
import styled from "styled-components";

const PageWrapper = styled.div`
  padding-top: 120px;
  min-height: 100vh;
  background: #fff;
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  color: var(--text-dark);
  margin-bottom: 20px;
  font-family: "Phudu", sans-serif;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const LastUpdated = styled.p`
  color: var(--text-light);
  font-style: italic;
`;

const Content = styled.div`
  h2 {
    color: var(--text-dark);
    margin: 40px 0 20px 0;
    font-family: "Phudu", sans-serif;
  }

  h3 {
    color: var(--text-dark);
    margin: 30px 0 15px 0;
    font-family: "Phudu", sans-serif;
  }

  p {
    color: var(--text-light);
    line-height: 1.7;
    margin-bottom: 20px;
  }

  ul,
  ol {
    color: var(--text-light);
    line-height: 1.7;
    margin-bottom: 20px;
    padding-left: 20px;
  }

  li {
    margin-bottom: 8px;
  }

  strong {
    color: var(--text-dark);
  }
`;

const HighlightBox = styled.div`
  background: var(--secondary-color);
  color: white;
  padding: 20px;
  border-radius: 10px;
  margin: 30px 0;
  text-align: center;

  h3 {
    color: white;
    margin-bottom: 10px;
  }

  p {
    color: white;
    margin: 0;
    opacity: 0.9;
  }
`;

const ContactInfo = styled.div`
  background: #f8f9fa;
  padding: 30px;
  border-radius: 15px;
  margin-top: 40px;
  text-align: center;

  h3 {
    color: var(--text-dark);
    margin-bottom: 20px;
    font-family: "Phudu", sans-serif;
  }

  p {
    color: var(--text-light);
    margin-bottom: 10px;
  }

  a {
    color: var(--secondary-color);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const ReturnRefundPolicy = () => {
  return (
    <PageWrapper>
      <Container>
        <PageHeader>
          <PageTitle>Return & Refund Policy</PageTitle>
          <LastUpdated>Last updated: October 14, 2025</LastUpdated>
        </PageHeader>

        <Content>
          <HighlightBox>
            <h3>Important Notice</h3>
            <p>
              Due to the nature of shipping containers and logistics involved,
              all sales are final upon delivery and acceptance.
            </p>
          </HighlightBox>

          <h2>1. General Policy</h2>
          <p>
            All container sales are final. Given the size, weight, and logistics
            involved in shipping container transactions, Baypoint Storage does
            not accept returns on containers once they have been delivered and
            accepted by the customer.
          </p>

          <h2>2. Pre-Delivery Inspection</h2>
          <p>To ensure customer satisfaction, we encourage all customers to:</p>
          <ul>
            <li>Review detailed photos and specifications before purchase</li>
            <li>Ask questions about container condition and features</li>
            <li>Request additional information or images if needed</li>
            <li>Understand the grade and condition of used containers</li>
          </ul>

          <h2>3. Delivery Acceptance</h2>
          <p>
            Upon delivery, customers have the opportunity to inspect the
            container. By accepting delivery, you acknowledge that:
          </p>
          <ul>
            <li>The container matches the description and specifications</li>
            <li>Any visible condition issues are acceptable</li>
            <li>The container is suitable for your intended use</li>
            <li>You waive the right to return the container</li>
          </ul>

          <h2>4. Refund Exceptions</h2>
          <p>
            Refunds may be considered only in the following exceptional
            circumstances:
          </p>

          <h3>Delivery Issues</h3>
          <ul>
            <li>
              Container significantly differs from description or specifications
            </li>
            <li>
              Structural damage that renders container unusable (not disclosed)
            </li>
            <li>Wrong size or type of container delivered</li>
            <li>Delivery to wrong location due to company error</li>
          </ul>

          <h3>Company Error</h3>
          <ul>
            <li>Billing errors or overcharges</li>
            <li>Duplicate charges</li>
            <li>Cancellation prior to delivery (with advance notice)</li>
          </ul>

          <h2>5. Refund Process</h2>
          <p>
            If you believe you qualify for a refund under our exceptional
            circumstances policy:
          </p>
          <ol>
            <li>
              <strong>Contact us immediately</strong> upon discovery of the
              issue
            </li>
            <li>
              <strong>Document the issue</strong> with photos and detailed
              description
            </li>
            <li>
              <strong>Do not move or modify</strong> the container
            </li>
            <li>
              <strong>Allow inspection</strong> by our team if requested
            </li>
          </ol>

          <h3>Refund Timeline</h3>
          <p>
            If a refund is approved, it will be processed within 7-10 business
            days to the original payment method. Shipping and delivery costs are
            non-refundable unless the error was on our part.
          </p>

          <h2>6. Rental Returns</h2>
          <p>For container rentals, different terms apply:</p>
          <ul>
            <li>
              Containers must be returned in the same condition as delivered
            </li>
            <li>
              Customer is responsible for cleaning and any damage beyond normal
              wear
            </li>
            <li>
              Early termination may incur fees as specified in rental agreement
            </li>
            <li>
              Deposit refunds are processed within 7-10 business days after
              inspection
            </li>
          </ul>

          <h2>7. Modification Services</h2>
          <p>
            Custom modifications are non-refundable as they are performed to
            customer specifications. We encourage thorough review of
            modification plans before approval.
          </p>

          <h2>8. Warranty Claims</h2>
          <p>
            Warranty claims for structural defects or wind/watertight issues
            should be reported within 30 days of delivery. Warranty claims are
            handled separately from returns and may include repair or
            replacement rather than refund.
          </p>

          <h2>9. Force Majeure</h2>
          <p>
            In cases where delivery cannot be completed due to natural
            disasters, government regulations, or other circumstances beyond our
            control, we will work with customers to reschedule delivery or
            provide appropriate refunds.
          </p>

          <h2>10. Dispute Resolution</h2>
          <p>
            If you are not satisfied with our resolution of your return or
            refund request, disputes will be handled according to the dispute
            resolution process outlined in our Terms & Conditions.
          </p>

          <HighlightBox>
            <h3>Questions Before Purchase?</h3>
            <p>
              Contact us before purchasing to ensure the container meets your
              needs. Our team is happy to provide additional information and
              photos.
            </p>
          </HighlightBox>

          <ContactInfo>
            <h3>Contact Us</h3>
            <p>
              If you have any questions about our Return & Refund Policy, please
              contact us:
            </p>
            <p>
              <strong>Email:</strong>{" "}
              <a href="mailto:returns@baypointstorage.com">
                returns@baypointstorage.com
              </a>
            </p>
            <p>
              <strong>Phone:</strong>{" "}
              <a href="tel:+14845429203">(484) 542-9203</a>
            </p>
            <p>
              <strong>Address:</strong> Baypoint Storage, Portage, Indiana,
              United States
            </p>
          </ContactInfo>
        </Content>
      </Container>
    </PageWrapper>
  );
};

export default ReturnRefundPolicy;
